'use strict';

const libPictProvider = require('pict-provider');

const BODY_MOUNT = '#PCW-Body';
const MESSAGE_MOUNT = '#PCW-Message';
// CustomProperties lives in its OWN mount (a sibling of #PCW-Body) so a body repaint never tears down the
// editable object-editor tree mounted inside it (same split as the Edit workspace).
const CUSTOMPROPS_MOUNT = '#PCW-CustomProps';

// The editable Project fields offered at CREATE, grouped for the form. A new project is never Active, so
// nothing is gated (every field editable) — the difference from the Edit workspace. Name is required; the
// rest are optional and default on the server. IDCustomer is NEVER sent (the server stamps it from session).
const FIELD_GROUPS =
[
	{ Name: 'Identity', Fields:
		[
			{ Key: 'Name', Label: 'Project Name', Type: 'text', Wide: true, Required: true },
			{ Key: 'Status', Label: 'Status', Type: 'text' },
			{ Key: 'ExternalProjectID', Label: 'External Project ID', Type: 'text' },
		] },
	{ Name: 'Relationships', Fields:
		[
			{ Key: 'IDProjectType', Label: 'Project Type', Type: 'entity', Entity: 'ProjectType', Wide: true },
			{ Key: 'IDOrganization', Label: 'Prime Contractor', Type: 'entity', Entity: 'Organization', Wide: true },
			{ Key: 'IDContract', Label: 'Contract', Type: 'entity', Entity: 'Contract', Wide: true },
		] },
	{ Name: 'Schedule & Financials', Fields:
		[
			{ Key: 'InitialContractAmount', Label: 'Initial Contract Amount', Type: 'number' },
			{ Key: 'CostPerDay', Label: 'Cost / Day', Type: 'number' },
			{ Key: 'OriginalContractedDays', Label: 'Original Contracted Days', Type: 'number' },
			{ Key: 'ContractedDays', Label: 'Contracted Days', Type: 'number' },
		] },
	{ Name: 'Description', Fields:
		[
			{ Key: 'Description', Label: 'Description', Type: 'textarea', Wide: true },
		] },
];

// Server-side entity-picker config per relationship field (mirrors ProjectEditWorkspace-Provider). The
// picker searches on the server through HeadlightRestClient (this app has no pict.EntityProvider).
const PICKER_PAGE_SIZE = 25;
const ENTITY_CONFIG =
{
	IDOrganization: { Set: 'Organizations', One: 'Organization', IDField: 'IDOrganization', Noun: 'prime contractor', SearchFields: [ 'Name', 'City', 'State', 'VendorCode', 'Type' ], IgnoreFields: [ 'IDCustomer', 'GUIDOrganization' ] },
	IDContract: { Set: 'Contracts', One: 'Contract', IDField: 'IDContract', Noun: 'contract', SearchFields: [ 'Name', 'Description', 'PrimaryProjectNumber', 'ExternalContractID', 'Status' ], IgnoreFields: [ 'IDCustomer', 'GUIDContract' ] },
	// ProjectType is customer-scoped but has NO Deleted column — NoSoftDelete drops the `Deleted~EQ~0` clause the
	// picker otherwise appends, which would make the ProjectTypes query fail.
	IDProjectType: { Set: 'ProjectTypes', One: 'ProjectType', IDField: 'IDProjectType', Noun: 'project type', SearchFields: [ 'Name', 'Description' ], IgnoreFields: [ 'IDCustomer', 'GUIDProjectType' ], NoSoftDelete: true },
};

/**
 * ProjectCreateWorkspace — the data layer behind the bespoke Project CREATE screen. Reached by the app-router
 * Create bridge (#/PSRS/Project/Create) that shadows the generic recordset create form, so the "+ New" button
 * opens the same curated, grouped experience as the View/Edit workspaces instead of the plain manifest form.
 *
 * It starts from a blank working copy, POSTs a new Project (IDCustomer is stamped by the server from the
 * session — never sent), and on success navigates to #/PSRS/Project/View/<newGUID>, which the View bridge
 * opens as the bespoke ProjectWorkspace. Field parity + the searchable entity pickers come straight from the
 * Edit workspace; the only structural difference is no Active-gating (a new project can't be Active).
 */
class ProjectCreateWorkspaceProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get state()
	{
		if (!this.pict.AppData.ProjectCreateWorkspace)
		{
			this.pict.AppData.ProjectCreateWorkspace =
			{
				Ready: false, Message: '', MessageClass: '', Saving: false,
				IDCustomer: 0, Title: 'New Project',
				Fields: {}, Errors: {}, CustomPropsData: {},
			};
		}
		return this.pict.AppData.ProjectCreateWorkspace;
	}

	// --- REST helpers ---

	_create(pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.createEntity('Project', pRecord, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	_toNumberOrNull(pValue)
	{
		if (pValue == null || String(pValue).trim() === '') { return null; }
		const tmpNumber = Number(pValue);
		return Number.isFinite(tmpNumber) ? tmpNumber : null;
	}

	// --- Start a new project ---

	/** Stash a one-shot prefill applied by the next startNew() (e.g. the Contract workspace's "New project"
	 *  passing { IDContract } so the created project is already linked to that contract). */
	prefillNew(pPrefill) { this._pendingPrefill = (pPrefill && typeof pPrefill === 'object') ? pPrefill : null; }

	/** Reset to a blank working copy and paint the empty form. Driven by the Create route bridge. */
	startNew()
	{
		const tmpState = this.state;
		tmpState.Message = '';
		tmpState.MessageClass = '';
		tmpState.Saving = false;
		tmpState.Errors = {};
		tmpState.Title = 'New Project';
		// Scope the entity pickers to the current user's customer (the server also scopes reads to MyCustomer;
		// this keeps parity with the Edit workspace and avoids a cross-customer browse for level-5 admins).
		tmpState.IDCustomer = Number(this.currentUser.CustomerID) || 0;
		const tmpFields = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			// Entity FKs start unset as '' (not 0) so the picker shows its placeholder, not a literal "0".
			tmpFields[pField.Key] = '';
		}));
		// A one-shot prefill (e.g. IDContract when created from the Contract workspace) seeds fields; the entity
		// pickers resolve a pre-set id to its display name on render.
		if (this._pendingPrefill)
		{
			Object.keys(this._pendingPrefill).forEach((pKey) => { if (pKey in tmpFields) { tmpFields[pKey] = this._pendingPrefill[pKey]; } });
			this._pendingPrefill = null;
		}
		tmpState.Fields = tmpFields;
		tmpState.CustomPropsData = {};
		// Reset the Location/map working state (center lives in Fields.Latitude/Longitude, unset at start).
		this._addressResults = [];
		this._addressSearching = false;
		this._addressError = '';
		this._locError = '';
		tmpState.Ready = true;
		this.render();
		this.renderCustomProps();
	}

	// --- Editing ---

	/** Update one working-copy field. onchange-driven, so no re-render (the DOM already holds the value). */
	setField(pKey, pValue)
	{
		this.state.Fields[pKey] = pValue;
		if (this.state.Errors[pKey]) { delete this.state.Errors[pKey]; }
	}

	_validate()
	{
		const tmpState = this.state;
		const tmpErrors = {};
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			const tmpValue = tmpState.Fields[pField.Key];
			const tmpHasValue = (tmpValue != null && String(tmpValue).trim() !== '');
			if (pField.Required && !tmpHasValue) { tmpErrors[pField.Key] = `${pField.Label} is required.`; return; }
			if (pField.Type === 'number' && tmpHasValue)
			{
				const tmpNumber = Number(tmpValue);
				if (!Number.isFinite(tmpNumber)) { tmpErrors[pField.Key] = `${pField.Label} must be a number.`; }
				else if (tmpNumber < 0) { tmpErrors[pField.Key] = `${pField.Label} can’t be negative.`; }
			}
		}));
		tmpState.Errors = tmpErrors;
		return Object.keys(tmpErrors).length === 0;
	}

	// --- Save / navigation ---

	async saveAsync()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		if (!this._validate())
		{
			tmpState.Message = 'Please fix the highlighted fields.';
			tmpState.MessageClass = 'is-danger';
			this.render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Message = 'Creating…';
		tmpState.MessageClass = 'is-info';
		this._renderMessage();
		try
		{
			// Build the create payload — omit empty optionals so the server applies its own defaults. IDCustomer
			// is intentionally never included (stamped from the session).
			const tmpPayload = {};
			FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
			{
				const tmpValue = tmpState.Fields[pField.Key];
				if ((pField.Type === 'number') || (pField.Type === 'entity'))
				{
					const tmpNumber = this._toNumberOrNull(tmpValue);
					if (tmpNumber != null) { tmpPayload[pField.Key] = tmpNumber; }
				}
				else if (tmpValue != null && String(tmpValue).trim() !== '')
				{
					tmpPayload[pField.Key] = tmpValue;
				}
			}));
			// Latitude/Longitude come from the map/Location section (not the FIELD_GROUPS form).
			const tmpLat = this._toNumberOrNull(tmpState.Fields.Latitude);
			const tmpLng = this._toNumberOrNull(tmpState.Fields.Longitude);
			if (tmpLat != null) { tmpPayload.Latitude = tmpLat; }
			if (tmpLng != null) { tmpPayload.Longitude = tmpLng; }
			if (tmpState.CustomPropsData && Object.keys(tmpState.CustomPropsData).length > 0)
			{
				tmpPayload.CustomProperties = JSON.stringify(tmpState.CustomPropsData);
			}
			const tmpSaved = await this._create(tmpPayload);
			if (!tmpSaved || tmpSaved.ErrorCode || !tmpSaved.IDProject)
			{
				throw new Error((tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode))
					? String(tmpSaved.Error || tmpSaved.ErrorCode)
					: 'the server rejected the new project (you may not have permission to create projects).');
			}
			tmpState.Saving = false;
			this._navigateToView(tmpSaved);
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Could not create the project: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this.render();
		}
	}

	cancel()
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = '#/PSRS/Project/List';
		}
	}

	/** Land on the bespoke read workspace for the just-created project (via the View bridge). */
	_navigateToView(pSaved)
	{
		if ((typeof window !== 'undefined') && window.location)
		{
			window.location.hash = pSaved.GUIDProject ? `#/PSRS/Project/View/${pSaved.GUIDProject}` : '#/PSRS/Project/List';
		}
	}

	// --- Geographic center / map (the Location section drives a Leaflet picker like the read workspace) ---

	/** The working center from the Fields (kept as Latitude/Longitude so save picks them up). */
	get center()
	{
		const tmpFields = this.state.Fields || {};
		return { Latitude: this._toNumberOrNull(tmpFields.Latitude), Longitude: this._toNumberOrNull(tmpFields.Longitude) };
	}

	get hasCenter()
	{
		const tmpCenter = this.center;
		return (tmpCenter.Latitude != null) && (tmpCenter.Longitude != null)
			&& !((Number(tmpCenter.Latitude) === 0) && (Number(tmpCenter.Longitude) === 0));
	}

	/**
	 * Set the working center (map click/drag, typed coords, or an address pick) — a LIGHT DOM update only
	 * (readout + input values), never a full re-render, so the live Leaflet map is not torn down under the
	 * user. pSyncMap moves the marker for non-map-driven changes (typed coords / address pick).
	 */
	setCenter(pLatitude, pLongitude, pSyncMap)
	{
		this.state.Fields.Latitude = Number(pLatitude);
		this.state.Fields.Longitude = Number(pLongitude);
		this._locError = '';
		this._syncCenterControls();
		if (pSyncMap)
		{
			const tmpMap = this.pict.views.ProjectCreateMap;
			if (tmpMap && (typeof tmpMap.moveMarkerTo === 'function')) { tmpMap.moveMarkerTo(Number(pLatitude), Number(pLongitude)); }
		}
	}

	/** Set the center from the editable lat/lng inputs (range-validated; bad input surfaces an inline error). */
	setCenterFromText(pLatitude, pLongitude)
	{
		const tmpLat = Number(pLatitude);
		const tmpLng = Number(pLongitude);
		if (!Number.isFinite(tmpLat) || !Number.isFinite(tmpLng) || (tmpLat < -90) || (tmpLat > 90) || (tmpLng < -180) || (tmpLng > 180))
		{
			this._locError = 'Enter a valid latitude (−90 to 90) and longitude (−180 to 180).';
			this._syncCenterControls();
			return;
		}
		this.setCenter(tmpLat, tmpLng, true);
	}

	/** Repaint just the readout + lat/lng input values (keeps the live map mounted). */
	_syncCenterControls()
	{
		if (typeof document === 'undefined') { return; }
		const tmpCenter = this.center;
		const tmpReadout = document.getElementById('PCW-LocReadout');
		if (tmpReadout) { tmpReadout.innerHTML = this._locReadoutHTML(); }
		const tmpLatEl = document.getElementById('PCW-LatInput');
		const tmpLngEl = document.getElementById('PCW-LngInput');
		if (tmpLatEl) { tmpLatEl.value = (tmpCenter.Latitude != null) ? String(tmpCenter.Latitude) : ''; }
		if (tmpLngEl) { tmpLngEl.value = (tmpCenter.Longitude != null) ? String(tmpCenter.Longitude) : ''; }
	}

	_locReadoutHTML()
	{
		const tmpCenter = this.center;
		const tmpText = this.hasCenter ? `${Number(tmpCenter.Latitude).toFixed(6)}, ${Number(tmpCenter.Longitude).toFixed(6)}` : 'Not set';
		const tmpErr = this._locError ? `<p class="pew-field-error">${this._esc(this._locError)}</p>` : '';
		return `<span>Geographic center: <code>${this._esc(tmpText)}</code></span>${tmpErr}`;
	}

	/** Geocode the address box via public Nominatim (same provider as the tiles — no API key). */
	async searchAddressAsync()
	{
		const tmpInput = (typeof document !== 'undefined') ? document.getElementById('PCW-AddressInput') : null;
		const tmpQuery = tmpInput ? String(tmpInput.value || '').trim() : '';
		if (!tmpQuery) { return; }
		this._addressSearching = true;
		this._addressError = '';
		this._addressResults = [];
		this._syncAddress();
		try
		{
			const tmpURL = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(tmpQuery)}`;
			const tmpResponse = await fetch(tmpURL, { headers: { 'Accept': 'application/json' } });
			if (!tmpResponse.ok) { throw new Error(`the geocoder returned ${tmpResponse.status}`); }
			const tmpRows = await tmpResponse.json();
			this._addressSearching = false;
			this._addressResults = (Array.isArray(tmpRows) ? tmpRows : [])
				.map((pRow) => ({ DisplayName: this._esc(pRow.display_name || 'result'), Latitude: Number(pRow.lat), Longitude: Number(pRow.lon) }))
				.filter((pRow) => Number.isFinite(pRow.Latitude) && Number.isFinite(pRow.Longitude))
				.slice(0, 5);
			this._addressError = (this._addressResults.length === 0) ? 'No matches found for that address.' : '';
			this._syncAddress();
		}
		catch (pError)
		{
			this._addressSearching = false;
			this._addressResults = [];
			this._addressError = `Address search failed: ${pError.message || pError}`;
			this._syncAddress();
		}
	}

	/** Pick an address result → drop the pin at its coordinates and clear the dropdown. */
	chooseAddressResult(pIndex)
	{
		const tmpRow = (this._addressResults || [])[Number(pIndex)];
		if (!tmpRow) { return; }
		this._addressResults = [];
		this._addressError = '';
		this.setCenter(tmpRow.Latitude, tmpRow.Longitude, true);
		this._syncAddress();
	}

	_syncAddress()
	{
		if (typeof document === 'undefined') { return; }
		const tmpEl = document.getElementById('PCW-AddressResults');
		if (tmpEl) { tmpEl.innerHTML = this._addressResultsHTML(); }
	}

	_addressResultsHTML()
	{
		const tmpStatus = this._addressSearching ? 'Searching…' : (this._addressError || '');
		const tmpStatusHTML = tmpStatus ? `<div class="pcw-addr-status${this._addressSearching ? '' : ' pcw-addr-status-error'}">${this._esc(tmpStatus)}</div>` : '';
		const tmpRows = (this._addressResults || []).map((pRow, pIndex) =>
			`<div class="pcw-addr-result" onclick="_Pict.providers.ProjectCreateWorkspace.chooseAddressResult(${pIndex}); return false;">${pRow.DisplayName}</div>`).join('');
		return tmpStatusHTML + tmpRows;
	}

	/** Mount the Leaflet picker into #PCW-MapHost (the ProjectCreateMap view reads this provider's center). */
	_mountMap()
	{
		if (!this.state.Ready) { return; }
		const tmpMap = this.pict.views.ProjectCreateMap;
		if (tmpMap && (typeof tmpMap.showForProject === 'function')) { tmpMap.showForProject(); }
	}

	// --- Render (hand-built HTML into the shell's mounts; mirrors ProjectEditWorkspace-Provider) ---

	/** Paint the whole create body into #PCW-Body. Called on start / save error (not on every keystroke). */
	render()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
		if (this.state.Ready) { this._mountPickers(); this._mountMap(); }
	}

	_mountPickers()
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			if (pField.Type === 'entity') { this._mountPicker(pField.Key); }
		}));
	}

	_mountPicker(pKey)
	{
		const tmpMount = `#PCW-Picker-${pKey}`;
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; }
		const tmpConfig = ENTITY_CONFIG[pKey];
		if (!tmpConfig) { return; }
		const tmpPicker = this.pict.providers['Pict-Section-Picker'].createPicker(`PCW-Picker-${pKey}`,
			{
				DestinationAddress: tmpMount,
				ValueAddress: `AppData.ProjectCreateWorkspace.Fields.${pKey}`,
				Searchable: true,
				AllowClear: true,
				ClearLabel: '(none)',
				Placeholder: `Search a ${tmpConfig.Noun}…`,
				DataProvider: this._entityDataProvider(tmpConfig),
				ResolveValue: this._entityResolveValue(tmpConfig),
				Entity: tmpConfig.One,
				AllowFieldDecoration: true,
				DecorationIgnoreFields: tmpConfig.IgnoreFields,
			});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
	}

	_entityDataProvider(pConfig)
	{
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		return (pSearchTerm, pPage) =>
		{
			const tmpStart = (Number(pPage) || 0) * PICKER_PAGE_SIZE;
			// Most entities soft-delete; a few (e.g. ProjectType) have no Deleted column, so skip the clause for them.
			let tmpFilter = `FBV~IDCustomer~EQ~${this.state.IDCustomer}`;
			if (!pConfig.NoSoftDelete) { tmpFilter += `~FBV~Deleted~EQ~0`; }
			const tmpTerm = String(pSearchTerm == null ? '' : pSearchTerm).trim();
			if (tmpTerm && tmpPickerProvider && (typeof tmpPickerProvider.buildSearchFilter === 'function'))
			{
				tmpFilter += `~${tmpPickerProvider.buildSearchFilter(pConfig.SearchFields, tmpTerm)}`;
			}
			return this._getJSON(`${pConfig.Set}/FilteredTo/${tmpFilter}/${tmpStart}/${PICKER_PAGE_SIZE}`)
				.then((pRows) =>
				{
					const tmpList = Array.isArray(pRows) ? pRows : [];
					return { results: tmpList.map((pRow) => ({ Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${pRow[pConfig.IDField]}`, Record: pRow })), hasMore: (tmpList.length >= PICKER_PAGE_SIZE) };
				})
				.catch(() => ({ results: [], hasMore: false }));
		};
	}

	_entityResolveValue(pConfig)
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			return this._getJSON(`${pConfig.One}/${tmpID}`)
				.then((pRow) => ((pRow && pRow[pConfig.IDField]) ? { Value: Number(pRow[pConfig.IDField]), Text: pRow.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` }))
				.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
		};
	}

	/** Paint the Custom Properties section into its own mount and mount the editable object-editor tree. */
	renderCustomProps()
	{
		if (!this.state.Ready) { return; }
		if (this.pict.ContentAssignment.getElement(CUSTOMPROPS_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(CUSTOMPROPS_MOUNT, this._customPropsSectionHTML());
		const tmpView = this.pict.views.ProjectCreateWorkspace;
		if (tmpView && (typeof tmpView.mountCustomProps === 'function')) { tmpView.mountCustomProps(); }
	}

	_customPropsSectionHTML()
	{
		return `<div class="pew-form pew-customprops-panel"><div class="pew-group pew-group-customprops">`
			+ `<div class="pew-group-head">Custom Properties</div>`
			+ `<div class="pew-customprops-help">Optional. Double-click a value to edit; use the + controls to add properties.</div>`
			+ `<div id="PCW-CustomPropsHost" class="pew-objecteditor-host"></div>`
			+ `</div></div>`;
	}

	_renderMessage()
	{
		if (this.pict.ContentAssignment.getElement(MESSAGE_MOUNT).length === 0) { this.render(); return; }
		this.pict.ContentAssignment.assignContent(MESSAGE_MOUNT, this._messageHTML());
	}

	_messageHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Message) { return ''; }
		return `<div class="notification ${this._esc(tmpState.MessageClass || 'is-info')} is-soft py-2 px-3 mb-3">${this._esc(tmpState.Message)}</div>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Ready)
		{
			return `<div class="pew-empty box has-text-grey has-text-centered"><span class="pew-spinner"></span><p class="mt-3">Loading…</p></div>`;
		}
		return `<div class="pew-head">`
			+ `<div><div class="pew-eyebrow">New Project</div><h1 class="pew-title">Create a project</h1></div>`
			+ `<div class="pew-head-actions">`
			+ `<button class="button is-link"${tmpState.Saving ? ' disabled' : ''} onclick="_Pict.providers.ProjectCreateWorkspace.saveAsync(); return false;"><span>Create project</span></button>`
			+ `<button class="button is-light" onclick="_Pict.providers.ProjectCreateWorkspace.cancel(); return false;"><span>Cancel</span></button>`
			+ `</div></div>`
			+ `<div id="PCW-Message">${this._messageHTML()}</div>`
			+ `<div class="pew-form">${FIELD_GROUPS.map((pGroup) => this._groupHTML(pGroup)).join('')}</div>`
			+ this._locationSectionHTML();
	}

	/** The Location section: address search + editable lat/lng + a Leaflet map picker (click/drag to set). */
	_locationSectionHTML()
	{
		const tmpCenter = this.center;
		const tmpLat = (tmpCenter.Latitude != null) ? String(tmpCenter.Latitude) : '';
		const tmpLng = (tmpCenter.Longitude != null) ? String(tmpCenter.Longitude) : '';
		return `<div class="pew-form pcw-loc-panel"><div class="pew-group">`
			+ `<div class="pew-group-head">Location</div>`
			+ `<div class="pcw-loc-search"><div class="pcw-loc-search-row">`
			+ `<input id="PCW-AddressInput" class="input pcw-addr-input" type="text" placeholder="Search an address or place…" autocomplete="off" onkeydown="if (event.key === 'Enter') { event.preventDefault(); _Pict.providers.ProjectCreateWorkspace.searchAddressAsync(); }" />`
			+ `<button class="button is-light" onclick="_Pict.providers.ProjectCreateWorkspace.searchAddressAsync(); return false;"><span>Search</span></button>`
			+ `</div><div id="PCW-AddressResults">${this._addressResultsHTML()}</div></div>`
			+ `<div class="pcw-loc-coords">`
			+ `<label class="pcw-loc-coord"><span>Latitude</span><input id="PCW-LatInput" class="input" type="number" step="any" placeholder="—" value="${this._esc(tmpLat)}" onchange="_Pict.providers.ProjectCreateWorkspace.setCenterFromText(this.value, (document.getElementById('PCW-LngInput')||{}).value)" /></label>`
			+ `<label class="pcw-loc-coord"><span>Longitude</span><input id="PCW-LngInput" class="input" type="number" step="any" placeholder="—" value="${this._esc(tmpLng)}" onchange="_Pict.providers.ProjectCreateWorkspace.setCenterFromText((document.getElementById('PCW-LatInput')||{}).value, this.value)" /></label>`
			+ `</div>`
			+ `<div id="PCW-LocReadout" class="pcw-loc-readout">${this._locReadoutHTML()}</div>`
			+ `<div id="PCW-MapHost" class="pcw-map-host"></div>`
			+ `<p class="pew-customprops-help">Search an address, type coordinates, or click the map / drag the marker to set this project’s location. Optional.</p>`
			+ `</div></div>`;
	}

	_groupHTML(pGroup)
	{
		const tmpRows = pGroup.Fields.map((pField) => this._fieldHTML(pField)).join('');
		return `<div class="pew-group"><div class="pew-group-head">${this._esc(pGroup.Name)}</div><div class="pew-grid">${tmpRows}</div></div>`;
	}

	_fieldHTML(pField)
	{
		const tmpState = this.state;
		const tmpValue = tmpState.Fields[pField.Key];
		const tmpError = tmpState.Errors[pField.Key] ? `<div class="pew-field-error">${this._esc(tmpState.Errors[pField.Key])}</div>` : '';
		const tmpWide = pField.Wide ? ' pew-field-wide' : '';
		return `<div class="pew-field${tmpWide}"><label class="pew-field-label">${this._esc(pField.Label)}</label>${this._controlHTML(pField, tmpValue)}${tmpError}</div>`;
	}

	_controlHTML(pField, pValue)
	{
		const tmpOnChange = `onchange="_Pict.providers.ProjectCreateWorkspace.setField('${pField.Key}', this.value)"`;
		if (pField.Type === 'textarea')
		{
			return `<textarea class="textarea pew-input" ${tmpOnChange}>${this._esc(pValue)}</textarea>`;
		}
		if (pField.Type === 'entity')
		{
			return `<div id="PCW-Picker-${pField.Key}" class="pew-input pew-picker-host"></div>`;
		}
		const tmpStep = (pField.Type === 'number') ? ' step="any"' : '';
		return `<input class="input pew-input" type="${(pField.Type === 'number') ? 'number' : 'text'}"${tmpStep} value="${this._esc(pValue)}" ${tmpOnChange} />`;
	}
}

module.exports = ProjectCreateWorkspaceProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ProjectCreateWorkspace',
	AutoInitialize: true,
	AutoInitializeOrdinal: 222,
};
